import { TbArrowRight, TbCheck } from "solid-icons/tb";
import { type Component, For } from "solid-js";
import { BrandGlyph } from "~/components/marketing/BrandGlyph";
import { useScene } from "~/hooks/useScene";
import { useI18n } from "~/i18n";
import { at, Crown, MockScene, PaneLabel } from "./internal/parts";
import { ScaledFrame } from "./ScaledFrame";

const NOTION = "/images/logos/notion.svg";

export type RoundTripData = {
  page: string;
  before: { time: string; title: string; lines: string[] };
  after: { time: string; heading: string; lines: string[] };
};

const T = { brief: 0.3, line: 0.8, summary: 3.0, summaryLine: 3.5, written: 5.0 };

/**
 * One meeting's Notion page on both sides of the call, played once: Chief's
 * prep brief before it, and the summary written back to the page after it.
 */
export const RoundTripMockup: Component<{ data: RoundTripData; class?: string }> = (props) => {
  const { t } = useI18n();
  const scene = useScene();
  return (
    <MockScene scene={scene} label={t("marketing.mock.roundtrip.alt")} class={props.class}>
      <ScaledFrame
        chrome="window"
        width={760}
        height={440}
        narrow={{ width: 400, height: 640 }}
        title={props.data.page}
      >
        <div class="grid h-full grid-rows-[auto_minmax(0,1fr)] gap-4 bg-canvas p-5 @2xl:grid-cols-[minmax(0,1fr)_auto_minmax(0,1.15fr)] @2xl:grid-rows-1">
          <div class="flex min-w-0 flex-col gap-2.5">
            <PaneLabel>
              {t("marketing.mock.roundtrip.before")} · <span class="font-normal">{props.data.before.time}</span>
            </PaneLabel>
            <div class="card-appear rounded-xl border border-brand-border bg-brand/5 p-4" style={at(T.brief)}>
              <p class="flex items-center gap-2 text-sm font-semibold">
                <Crown class="size-4" />
                {props.data.before.title}
              </p>
              <ul class="mt-3 flex flex-col gap-2 text-[13px] leading-snug">
                <For each={props.data.before.lines}>
                  {(line, i) => (
                    <li class="msg-in flex gap-2" style={at(T.line + i() * 0.35)}>
                      <span class="mt-1.5 size-1 shrink-0 rounded-full bg-brand" />
                      {line}
                    </li>
                  )}
                </For>
              </ul>
              <p class="mt-3 flex items-center gap-1.5 text-xs text-muted-text">
                {t("marketing.mock.ask.from")}
                <BrandGlyph src={NOTION} class="size-3.5 text-primary-text" />
                {props.data.page}
              </p>
            </div>
          </div>

          <span class="hidden place-items-center self-center text-muted-text @2xl:grid">
            <TbArrowRight class="size-5" aria-hidden="true" />
          </span>

          <div class="flex min-w-0 flex-col gap-2.5">
            <PaneLabel>
              {t("marketing.mock.roundtrip.after")} · <span class="font-normal">{props.data.after.time}</span>
            </PaneLabel>
            <div class="flex flex-1 flex-col rounded-xl border border-primary-border bg-tile p-4">
              <p class="flex items-center gap-2 text-lg font-bold tracking-tight">
                <BrandGlyph src={NOTION} class="size-5 text-primary-text" />
                {props.data.page}
              </p>
              <div class="mt-3 flex flex-col gap-2">
                <span class="h-2 w-full rounded-full bg-secondary-text/15" />
                <span class="h-2 w-4/5 rounded-full bg-secondary-text/15" />
              </div>
              <div class="card-appear mt-4 border-s-2 border-brand ps-3" style={at(T.summary)}>
                <p class="flex items-center gap-2 text-sm font-semibold">
                  {props.data.after.heading}
                  <span class="flex items-center gap-1 text-xs font-normal text-muted-text">
                    <Crown class="size-3" />
                    {t("marketing.mock.roundtrip.by_chief")}
                  </span>
                </p>
                <ul class="mt-2 flex flex-col gap-1.5 text-[13px] leading-snug text-secondary-text">
                  <For each={props.data.after.lines}>
                    {(line, i) => (
                      <li class="msg-in flex gap-2" style={at(T.summaryLine + i() * 0.35)}>
                        <span class="mt-1.5 size-1 shrink-0 rounded-full bg-secondary-text/60" />
                        {line}
                      </li>
                    )}
                  </For>
                </ul>
              </div>
              <p
                class="pop-in mt-auto flex items-center gap-1 self-end pt-3 text-xs font-medium text-green-600 dark:text-green-400"
                style={at(T.written)}
              >
                <TbCheck class="size-3.5" aria-hidden="true" />
                {t("marketing.mock.exec.written_back")}
              </p>
            </div>
          </div>
        </div>
      </ScaledFrame>
    </MockScene>
  );
};
